import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "mt-6" }
const _hoisted_2 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_3 = {
  key: 1,
  class: "space-y-3"
}
const _hoisted_4 = { class: "text-xs text-black" }
const _hoisted_5 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_6 = { class: "min-w-full border border-gray-300 rounded-md" }
const _hoisted_7 = { class: "px-4 py-2" }
const _hoisted_8 = { class: "flex items-center justify-between" }
const _hoisted_9 = ["href"]
const _hoisted_10 = { class: "text-black" }
const _hoisted_11 = ["onClick", "disabled"]

import { ref, onMounted } from "vue";
import {
  uploadInvoice,
  getUploadedInvoices,
  deleteInvoice,
} from "@/api/orders";
import { useNotification } from "@/composables/useNotification";
import ModalComp from "@/components/ui/ModalComp.vue";

// Props
interface Props {
  orderId: number;
  orderGroupId: number;
  orderDate: string;
  invoiceId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadZone',
  props: {
    orderId: {},
    orderGroupId: {},
    orderDate: {},
    invoiceId: {}
  },
  emits: ["invoiceUploaded", "invoicesCountChanged"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// État
const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement>();
const isUploading = ref(false);
const currentUploadingFile = ref<File | null>(null);
const uploadedFiles = ref<Array<{ filename: string; url: string }>>([]);
const isDeleting = ref(false);
const showDeleteModal = ref(false);
const invoiceToDelete = ref<string>("");

const { success, error: showError } = useNotification();

// Charger les factures existantes au montage
const loadExistingInvoices = async () => {
  try {
    const invoices = await getUploadedInvoices(props.orderId);
    uploadedFiles.value = invoices;
    // Émettre le nombre de factures après chargement
    emit("invoicesCountChanged", invoices.length);
  } catch (error) {
    emit("invoicesCountChanged", 0);
  }
};

// Méthodes
const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    await uploadFile(target.files[0]);
    // Réinitialiser l'input pour permettre le même fichier
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
};

const handleFileDrop = async (event: DragEvent) => {
  isDragOver.value = false;
  if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
    await uploadFile(event.dataTransfer.files[0]);
  }
};

// Validation du fichier avant upload
const validateFile = (file: File): { valid: boolean; error?: string } => {
  // Vérifier le format
  const allowedExts = [".pdf", ".jpg", ".jpeg", ".png"];
  const fileName = file.name.toLowerCase();
  const ext = fileName.substring(fileName.lastIndexOf("."));

  if (!allowedExts.includes(ext)) {
    return {
      valid: false,
      error: `Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG. Votre fichier : ${ext.toUpperCase()}`,
    };
  }

  // Vérifier la taille (10 MB max)
  const maxSize = 10 * 1024 * 1024; // 10 MB
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Fichier trop volumineux. Taille maximale autorisée : 10 MB. Taille de votre fichier : ${fileSizeMB} MB`,
    };
  }

  return { valid: true };
};

const uploadFile = async (file: File) => {
  if (isUploading.value) return;

  // Validation avant upload
  const validation = validateFile(file);
  if (!validation.valid) {
    showError(validation.error || "Fichier invalide");
    return;
  }

  isUploading.value = true;
  currentUploadingFile.value = file;

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("idCommande", String(props.orderId));

    const result = await uploadInvoice(formData);

    if (result.success) {
      success("Facture uploadée avec succès");
      emit("invoiceUploaded", "upload");
      // Émettre un événement personnalisé pour les composants de labels
      window.dispatchEvent(
        new CustomEvent("invoiceUploaded", {
          detail: { orderId: props.orderId },
        }),
      );
      // Recharger la liste des factures
      await loadExistingInvoices();
    }
  } catch (err) {
    // Extraire le message d'erreur de manière plus explicite
    let errorMessage = "Erreur lors de l'upload de la facture";

    if (err instanceof Error) {
      const errorText = err.message;

      // Détecter les erreurs spécifiques
      if (
        errorText.includes("fileSize") ||
        errorText.includes("too large") ||
        errorText.includes("volumineux")
      ) {
        errorMessage =
          "Fichier trop volumineux. La taille maximale autorisée est de 10 MB.";
      } else if (
        errorText.includes("Type de fichier non autorisé") ||
        errorText.includes("format")
      ) {
        errorMessage =
          "Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG.";
      } else if (errorText.includes("Aucun fichier fourni")) {
        errorMessage =
          "Aucun fichier n'a été sélectionné. Veuillez choisir un fichier à uploader.";
      } else if (errorText.includes("Commande introuvable")) {
        errorMessage =
          "Commande introuvable ou vous n'avez pas l'autorisation d'uploader une facture pour cette commande.";
      } else if (
        errorText.includes("HTTP: 413") ||
        errorText.includes("Request Entity Too Large")
      ) {
        errorMessage =
          "Fichier trop volumineux. La taille maximale autorisée est de 10 MB.";
      } else if (errorText.includes("HTTP: 400")) {
        // Extraire le message du backend si disponible
        const match = errorText.match(/HTTP: 400 - (.+)/);
        if (match && match[1]) {
          errorMessage = match[1];
        } else {
          errorMessage =
            "Erreur lors de l'upload. Veuillez vérifier le format et la taille de votre fichier.";
        }
      } else {
        // Utiliser le message d'erreur tel quel s'il est explicite
        errorMessage = errorText;
      }
    }

    showError(errorMessage);
  } finally {
    isUploading.value = false;
    currentUploadingFile.value = null;
  }
};

const handleDeleteInvoice = (filename: string) => {
  if (isDeleting.value) return;
  invoiceToDelete.value = filename;
  showDeleteModal.value = true;
};

const confirmDeleteInvoice = async () => {
  if (!invoiceToDelete.value) return;

  isDeleting.value = true;
  try {
    await deleteInvoice(props.orderId, invoiceToDelete.value);
    success("Facture supprimée avec succès");
    emit("invoiceUploaded", "delete");
    // Émettre un événement personnalisé pour les composants de labels
    window.dispatchEvent(
      new CustomEvent("invoiceDeleted", { detail: { orderId: props.orderId } }),
    );
    // Recharger la liste des factures
    await loadExistingInvoices();
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la suppression de la facture",
    );
  } finally {
    isDeleting.value = false;
    invoiceToDelete.value = "";
  }
};

onMounted(() => {
  loadExistingInvoices();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm text-black mb-2" }, "Zone d'upload :", -1)),
    _createElementVNode("div", {
      class: _normalizeClass(["border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors cursor-pointer", isDragOver.value ? 'border-blue-400 bg-blue-50' : 'border-gray-300']),
      onDragover: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isDragOver.value = true), ["prevent"])),
      onDragleave: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragOver.value = false), ["prevent"])),
      onDrop: _withModifiers(handleFileDrop, ["prevent"]),
      onClick: triggerFileInput
    }, [
      _createElementVNode("input", {
        ref_key: "fileInput",
        ref: fileInput,
        type: "file",
        accept: ".pdf,.jpg,.jpeg,.png",
        class: "hidden",
        onChange: handleFileSelect
      }, null, 544),
      (!isUploading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[3] || (_cache[3] = [
            _createElementVNode("i", { class: "fas fa-cloud-upload-alt text-black text-3xl" }, null, -1),
            _createElementVNode("div", null, [
              _createElementVNode("p", { class: "text-sm font-medium text-black" }, " Glisser la facture dans cette zone ou cliquez pour sélectionner un fichier ")
            ], -1),
            _createElementVNode("p", { class: "text-xs text-black mt-1" }, " Formats acceptés : PDF, JPG, JPEG, PNG • Taille maximale : 10 MB ", -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fas fa-spinner fa-spin text-blue-500 text-3xl" }, null, -1)),
            _createElementVNode("div", null, [
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-sm font-medium text-black" }, "Upload en cours...", -1)),
              _createElementVNode("p", _hoisted_4, _toDisplayString(currentUploadingFile.value?.name), 1)
            ])
          ]))
    ], 34),
    (uploadedFiles.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("table", _hoisted_6, [
            _cache[8] || (_cache[8] = _createElementVNode("thead", { class: "bg-gray-50" }, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", { class: "px-4 py-2 text-left text-sm font-medium text-black border-b" }, " Facture existante ")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uploadedFiles.value, (file, index) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: index,
                  class: "border-b hover:bg-gray-50"
                }, [
                  _createElementVNode("td", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("a", {
                        href: file.url,
                        target: "_blank",
                        class: "text-sm text-blue-600 hover:underline flex items-center gap-2"
                      }, [
                        _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-file-pdf text-red-500" }, null, -1)),
                        _createElementVNode("span", _hoisted_10, _toDisplayString(file.filename), 1)
                      ], 8, _hoisted_9),
                      _createElementVNode("button", {
                        onClick: _withModifiers(($event: any) => (handleDeleteInvoice(file.filename)), ["stop"]),
                        disabled: isDeleting.value,
                        class: "px-2 py-1 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      }, _cache[7] || (_cache[7] = [
                        _createElementVNode("i", { class: "fas fa-trash" }, null, -1)
                      ]), 8, _hoisted_11)
                    ])
                  ])
                ]))
              }), 128))
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(ModalComp, {
      "is-visible": showDeleteModal.value,
      title: "Supprimer la facture",
      content: "Voulez-vous vraiment supprimer cette facture ?",
      "cancel-text": "Annuler",
      "confirm-text": "Supprimer",
      size: "sm",
      "onUpdate:isVisible": _cache[2] || (_cache[2] = ($event: any) => (showDeleteModal.value = $event)),
      onConfirm: confirmDeleteInvoice
    }, null, 8, ["is-visible"])
  ]))
}
}

})